// @ts-check
import { Plural, Trans } from '@lingui/macro';
import React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Flex, SkeletonText, Text } from '~/design-system';
import Footer, {
  BackButton,
  CancelButton,
  NextButton,
} from '../../../../../components/Layout/Footer';
import Layout from '../../../../../components/Layout/Layout';
import Main from '../../../../../components/Layout/Main';
import {
  PageHeader,
  PageSubtitle,
  PageTitle,
} from '../../../../../components/Layout/PageHeader';
import SuppliersOverview, {
  useMatchSuppliers,
} from '../../../../../components/MapSuppliers/SuppliersOverview';
import { ProgressIndicator, useLink } from '../_config';

export default function All() {
  const history = useHistory();
  const link = useLink('suppliers');
  const { path } = useRouteMatch();
  /** @type {{ params : { importId: string, tab: string } } | null} */
  // We pull out the selected tab manually from the route
  const manualMatch = useRouteMatch({
    path: path.replace(/\/\w+\/?$/, '/:tab'),
  });
  const props = useMatchSuppliers({
    workspaceId: manualMatch?.params.workspaceId,
    importId: manualMatch?.params.importId,
    tab: manualMatch?.params.tab,
  });

  async function onSelectSupplier(supplierName) {
    //We save all suppliers map when next button is clicked.
    //The flow can be skipped by matching or rematching a supplier.
    //Here we ensure that we save the mapped suppliers from other imports
    //when the match or rematch button is clicked.
    const mappedPartners = Object.values(props?.suppliersMap);
    if (mappedPartners?.length > props?.savedSuppliers?.length) {
      await props?.saveSuppliersMatch?.();
    }
    history.push(link._all.supplier({ supplierName }));
  }

  return (
    <Layout>
      <ProgressIndicator step="matchSuppliers" />
      <PageHeader>
        <PageTitle>
          <Trans>Match producers</Trans>
        </PageTitle>
        <PageSubtitle>
          {props.loading ? (
            <Flex sx={{ justifyContent: 'space-around' }}>
              <SkeletonText width={100} />
            </Flex>
          ) : props.unmatchedSuppliers?.length === 0 ? (
            <Text variant="default">
              <Trans>All producers matched successfully</Trans>
            </Text>
          ) : (
            <React.Fragment>
              <Text variant="default">
                <Plural
                  value={props.unmatchedSuppliers?.length}
                  one={'1 producer needs to be matched.'}
                  other={'# producers need to be matched.'}
                />
              </Text>
              <PageSubtitle>
                <Trans>
                  Please match producer names to Bottlebooks companies.
                </Trans>
              </PageSubtitle>
            </React.Fragment>
          )}
        </PageSubtitle>
      </PageHeader>
      <Main>
        <SuppliersOverview
          sx={{
            margin: '0 auto',
            marginTop: 3,
          }}
          tab={manualMatch?.params.tab}
          onSelectSupplier={onSelectSupplier}
          link={link}
          {...props}
        />
      </Main>
      <Footer>
        <BackButton to={link.back()} />
        <CancelButton to={link.cancel()} />
        <NextButton
          onClick={async () => {
            const result = await props?.saveSuppliersMatch?.();
            if (result && !result?.error) history.push(link.next());
          }}
          disabled={!props.isValid}
        />
      </Footer>
    </Layout>
  );
}
